import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useTranslation from "@babackapp/translations/use-translation";
import useFormatPrice from "@/hooks/use-format-price";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import { countryFromISO } from "@/utils/country";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { Alert, AlertDescription } from "@babackapp/ui/components/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@babackapp/ui/components/alert-dialog";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import {
  ArrowRight,
  ExternalLink,
  Info,
  Lock,
  PencilIcon,
  Plus,
  PlusCircle,
  TrashIcon,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import type {
  CarrierDto,
  ShopOverrideRuleDto,
} from "@babackapp/private-api-client/types";
import {
  OverrideRulesProvider,
  useOverrideRules,
} from "@/components/override-rules/override-rules-context";
import { OverrideRuleDialog } from "@/components/override-rules/override-rule-dialog";
import { OverrideRulesManager } from "@/components/override-rules";
import { PricingEdition } from "../forms/pricings-form";
import { useCarriersContext } from "../../use-carriers-context";
import type { ScopeFormValues } from "@/lib/override-rules";
import type { CarrierOption } from "@/components/override-rules/scope-selector";

// Format rule conditions as a single readable string for tooltips
function conditionsSummary(
  conditions: ShopOverrideRuleDto["conditions"],
  t: (key: string) => string,
): string {
  const conds = conditions?.rules ?? [];
  if (conds.length === 0) return "";
  const sep =
    conditions?.operator === "OR" ? ` ${t("misc.or")} ` : ` ${t("misc.and")} `;
  const fmtDate = (v: unknown): string => {
    try {
      return new Date(v as string | number).toLocaleDateString(
        navigator.language,
        { day: "2-digit", month: "2-digit", year: "numeric" },
      );
    } catch {
      return String(v);
    }
  };

  return conds
    .map((c) => {
      const field = t(`overrideRules.fields.${c.id}.label`);
      const op = t(`overrideRules.operators.${c.operator}.label`);
      let val: string;
      if (c.type === "DATE") {
        val = Array.isArray(c.value)
          ? c.value.map(fmtDate).join(" — ")
          : fmtDate(c.value);
      } else if (Array.isArray(c.value)) {
        val = c.value.join(", ");
      } else if (typeof c.value === "boolean") {
        val = c.value ? "✓" : "✗";
      } else {
        val = String(c.value ?? "");
      }
      return `${field} ${op} ${val}`;
    })
    .join(sep);
}

// Unified carrier display: badge with code + optional identifier text outside (mirrors carrier-list-item)
function CarrierLabel({ carrier }: { carrier: CarrierDto }) {
  const identifier =
    carrier.preferences?.webSiteId ||
    carrier.preferences?.accountId ||
    carrier.preferences?.subAccount;
  return (
    <div className="flex items-center gap-2">
      <Badge
        variant="outline"
        className="bg-emerald-50 text-emerald-700 border-emerald-200 font-medium text-xs"
      >
        {carrier.code.toUpperCase()}
      </Badge>
      {identifier && (
        <span className="text-xs text-muted-foreground">{identifier}</span>
      )}
    </div>
  );
}

// ShopOverrideRuleDto now includes scope from the generated schema.
// We alias it here to keep call-sites readable.
type RuleWithScope = ShopOverrideRuleDto;

type RuleDialogState = {
  isOpen: boolean;
  existingRule?: ShopOverrideRuleDto;
  defaultScope?: ScopeFormValues;
};

type PricingState = {
  carrierId: string | null;
  pricingIndex: number; // -1 = creating new
};

type FooterState = {
  open: boolean;
  carrierId: string | null;
};

function ReturnFeePricingsCardContent() {
  const t = useTranslation();
  const formatPrice = useFormatPrice();
  const isAdmin = useViewerHasAtLeastAdminRights();
  const { carriers, shop, editCarrierPricingsTransport, deleteCarrierPricing } =
    useCarriersContext();
  const { rules, deleteRule, deleteRulePending } = useOverrideRules();

  const [ruleDialog, setRuleDialog] = useState<RuleDialogState>({
    isOpen: false,
  });
  const [manageOpen, setManageOpen] = useState(false);
  const [pricingState, setPricingState] = useState<PricingState>({
    carrierId: null,
    pricingIndex: -2, // -2 = nothing open, -1 = creating
  });
  const [footerState, setFooterState] = useState<FooterState>({
    open: false,
    carrierId: null,
  });

  // Carriers split by whether they have pricings
  const carriersWithPricings = useMemo(
    () => carriers.filter((c) => c.pricings.length > 0),
    [carriers],
  );
  const carriersWithoutPricings = useMemo(
    () => carriers.filter((c) => c.pricings.length === 0),
    [carriers],
  );

  // Filter OVERRIDE_RETURN_FEE rules
  const overrideRules = useMemo(
    () =>
      (rules as RuleWithScope[]).filter(
        (r) => r.action.type === "OVERRIDE_RETURN_FEE",
      ),
    [rules],
  );

  const globalRules = useMemo(
    () => overrideRules.filter((r) => !r.scope || r.scope.level === "GLOBAL"),
    [overrideRules],
  );

  // Pre-index rules by carrierId / pricingId to avoid O(n) filter per carrier row.
  const rulesByCarrierId = useMemo(() => {
    const map = new Map<string, RuleWithScope[]>();
    for (const rule of overrideRules) {
      if (rule.scope?.level === "CARRIER" && rule.scope.carrierId) {
        const list = map.get(rule.scope.carrierId) ?? [];
        list.push(rule);
        map.set(rule.scope.carrierId, list);
      }
    }
    return map;
  }, [overrideRules]);

  const rulesByPricingId = useMemo(() => {
    const map = new Map<string, RuleWithScope[]>();
    for (const rule of overrideRules) {
      if (rule.scope?.level === "TARIF" && rule.scope.pricingId) {
        const list = map.get(rule.scope.pricingId) ?? [];
        list.push(rule);
        map.set(rule.scope.pricingId, list);
      }
    }
    return map;
  }, [overrideRules]);

  const getCarrierRules = (carrierId: string) =>
    rulesByCarrierId.get(carrierId) ?? [];

  const getTarifRules = (pricingId: string) =>
    rulesByPricingId.get(pricingId) ?? [];

  const openRuleDialog = (opts: Omit<RuleDialogState, "isOpen">) =>
    setRuleDialog({ isOpen: true, ...opts });

  const closeRuleDialog = () => setRuleDialog((s) => ({ ...s, isOpen: false }));

  const closePricing = () =>
    setPricingState({ carrierId: null, pricingIndex: -2 });

  const closeFooter = () => setFooterState({ open: false, carrierId: null });

  const formatFeeValue = (rule: RuleWithScope) => {
    const value = rule.action.parameters?.value;
    if (typeof value !== "number") return "";
    if (value === 0) return t("settings.carriers.returnFeeOverride.free");
    if (value > 0)
      return formatPrice({
        amount: value,
        currency: shop.preferences.currency,
      });
    return "";
  };

  // Customer segments with free return — these override all rules
  const freeReturnSegments = useMemo(
    () =>
      (shop.preferences.customerSegments ?? []).filter((s) => s.hasFreeReturn),
    [shop.preferences.customerSegments],
  );

  // Build carriersData for the scope selector
  const carriersData = useMemo<CarrierOption[]>(
    () =>
      carriers.map((c) => ({
        id: c.id,
        name: c.code,
        pricings: (c.pricings ?? []).map((p) => ({
          id: p.id,
          origin: p.origin,
          destination: p.destination,
          price: p.price,
          currency: p.currency,
        })),
      })),
    [carriers],
  );

  const footerCarrier = useMemo(
    () =>
      footerState.carrierId
        ? carriersWithoutPricings.find((c) => c.id === footerState.carrierId)
        : null,
    [footerState.carrierId, carriersWithoutPricings],
  );

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            {t("settings.carriers.returnFeeOverride.title")}
          </CardTitle>
          <CardDescription>
            {t("settings.carriers.returnFeeOverride.description")}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* ── Customer segments (read-only, highest priority) ── */}
          {freeReturnSegments.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2.5">
              <div className="mb-2 flex items-center gap-1.5">
                <Lock className="h-3 w-3 text-amber-600" />
                <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                  {t(
                    "settings.carriers.returnFeeOverride.customerSegmentsSection",
                  )}
                </span>
              </div>
              <div className="mb-2 flex flex-wrap gap-1.5">
                {freeReturnSegments.map((segment) => (
                  <span
                    key={segment.name}
                    className="inline-flex cursor-default items-center gap-1.5 rounded-full border border-amber-200 bg-white px-2.5 py-0.5 text-xs font-medium text-amber-800"
                    title={t(
                      "settings.carriers.returnFeeOverride.customerSegmentReadOnly",
                    )}
                  >
                    ⭐ {segment.name} ·{" "}
                    {t("settings.carriers.returnFeeOverride.free")}
                  </span>
                ))}
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-amber-700">
                  {t(
                    "settings.carriers.returnFeeOverride.customerSegmentsFreeHint",
                  )}
                </p>
                <Link
                  to={`/shops/${shop.id}/settings/customer-portal#customer-segments`}
                  className="inline-flex shrink-0 items-center gap-1 text-xs text-amber-600 hover:text-amber-800 underline underline-offset-2"
                >
                  {t(
                    "settings.carriers.returnFeeOverride.customerSegmentsLink",
                  )}
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            </div>
          )}

          {/* Priority hint */}
          <Alert className="border-blue-100 bg-blue-50/60">
            <Info className="h-4 w-4 text-blue-500" />
            <AlertDescription className="text-blue-800 text-xs">
              {t("settings.carriers.returnFeeOverride.priorityHint")}
            </AlertDescription>
          </Alert>

          {/* ── Global rules row ── */}
          <div className="flex flex-wrap items-center gap-2 pb-1 border-b">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {t("settings.carriers.returnFeeOverride.globalSection")}
            </span>

            {globalRules.map((rule) => (
              <Badge
                key={rule.id}
                variant="outline"
                title={conditionsSummary(
                  rule.conditions,
                  t as (k: string) => string,
                )}
                className="cursor-pointer gap-1 text-xs font-normal py-0.5 bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100"
                onClick={() => openRuleDialog({ existingRule: rule })}
              >
                {rule.name}
                {formatFeeValue(rule) && (
                  <span className="font-medium"> — {formatFeeValue(rule)}</span>
                )}
                <PencilIcon className="h-2.5 w-2.5 ml-0.5 opacity-50" />
              </Badge>
            ))}

            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs px-2 text-violet-600 hover:text-violet-800 hover:bg-violet-50"
                onClick={() =>
                  openRuleDialog({
                    defaultScope: { level: "GLOBAL" },
                  })
                }
              >
                <Plus className="h-3 w-3 mr-1" />
                {t("settings.carriers.returnFeeOverride.addGlobalRule")}
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs px-2 ml-auto text-primary"
              onClick={() => setManageOpen(true)}
            >
              {t("settings.carriers.returnFeeOverride.manage")}
            </Button>
          </div>

          {/* ── Per-carrier sections (only carriers with pricings) ── */}
          <div className="space-y-3">
            {carriersWithPricings.map((carrier) => {
              const cRules = getCarrierRules(carrier.id);
              return (
                <CarrierReturnFeeSection
                  key={carrier.id}
                  carrier={carrier}
                  carrierRules={cRules}
                  getTarifRules={getTarifRules}
                  formatFeeValue={formatFeeValue}
                  isAdmin={isAdmin}
                  pricingState={pricingState}
                  onOpenPricing={(idx) =>
                    setPricingState({
                      carrierId: carrier.id,
                      pricingIndex: idx,
                    })
                  }
                  onClosePricing={closePricing}
                  onOpenCarrierRule={() =>
                    openRuleDialog({
                      defaultScope: {
                        level: "CARRIER",
                        carrierId: carrier.id,
                      },
                    })
                  }
                  onEditCarrierRule={(rule) =>
                    openRuleDialog({ existingRule: rule })
                  }
                  onDeleteCarrierRule={(rule) => deleteRule(rule.id)}
                  onOpenTarifRule={(pricingId) =>
                    openRuleDialog({
                      defaultScope: {
                        level: "TARIF",
                        carrierId: carrier.id,
                        pricingId,
                      },
                    })
                  }
                  onEditTarifRule={(rule) =>
                    openRuleDialog({ existingRule: rule })
                  }
                  deleteRulePending={deleteRulePending}
                  editCarrierPricingsTransport={editCarrierPricingsTransport}
                  deleteCarrierPricing={deleteCarrierPricing}
                  shop={shop}
                />
              );
            })}
          </div>

          {/* ── Add tarif on another carrier (footer) ── */}
          {isAdmin && carriersWithoutPricings.length > 0 && (
            <div className="border-t pt-3">
              {!footerState.open ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1.5 w-full justify-center border-dashed text-muted-foreground hover:text-foreground"
                  onClick={() =>
                    setFooterState({ open: true, carrierId: null })
                  }
                >
                  <PlusCircle className="h-3.5 w-3.5" />
                  {t(
                    "settings.carriers.returnFeeOverride.addTarifOnOtherCarrier",
                  )}
                </Button>
              ) : (
                <div className="rounded-lg border overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b gap-2">
                    {footerState.carrierId ? (
                      footerCarrier ? (
                        <CarrierLabel carrier={footerCarrier} />
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {footerState.carrierId}
                        </span>
                      )
                    ) : (
                      <Select
                        value={footerState.carrierId ?? ""}
                        onValueChange={(val) =>
                          setFooterState((s) => ({ ...s, carrierId: val }))
                        }
                      >
                        <SelectTrigger className="h-7 text-xs w-48">
                          <SelectValue
                            placeholder={t(
                              "settings.carriers.returnFeeOverride.selectCarrier",
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {carriersWithoutPricings.map((c) => {
                            const identifier =
                              c.preferences?.webSiteId ||
                              c.preferences?.accountId ||
                              c.preferences?.subAccount;
                            return (
                              <SelectItem key={c.id} value={c.id}>
                                <span className="flex items-center gap-2">
                                  <span className="font-medium">
                                    {c.code.toUpperCase()}
                                  </span>
                                  {identifier && (
                                    <span className="text-muted-foreground text-xs">
                                      {identifier}
                                    </span>
                                  )}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs px-2 text-muted-foreground ml-auto"
                      onClick={closeFooter}
                    >
                      {t("misc.cancel")}
                    </Button>
                  </div>

                  {footerCarrier && (
                    <div className="p-3 bg-gray-50">
                      <PricingEdition
                        carrier={footerCarrier}
                        shop={shop}
                        setEditingIsOpen={(open) => !open && closeFooter()}
                        onSubmit={(params) => {
                          editCarrierPricingsTransport(params);
                          closeFooter();
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rule create/edit dialog */}
      <OverrideRuleDialog
        isOpen={ruleDialog.isOpen}
        onOpenChange={(open) => !open && closeRuleDialog()}
        existingRule={ruleDialog.existingRule}
        actionType="OVERRIDE_RETURN_FEE"
        showScope
        carriers={carriersData}
        defaultScope={ruleDialog.defaultScope}
      />

      {/* Full management dialog */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {t("settings.carriers.returnFeeOverride.allRules")}
            </DialogTitle>
          </DialogHeader>
          <OverrideRulesManager
            actionType="OVERRIDE_RETURN_FEE"
            showScope
            carriers={carriersData}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Carrier section sub-component ──────────────────────────────────────────

interface CarrierReturnFeeSectionProps {
  carrier: CarrierDto;
  carrierRules: RuleWithScope[];
  getTarifRules: (pricingId: string) => RuleWithScope[];
  formatFeeValue: (rule: RuleWithScope) => string;
  isAdmin: boolean;
  pricingState: PricingState;
  onOpenPricing: (index: number) => void;
  onClosePricing: () => void;
  onOpenCarrierRule: () => void;
  onEditCarrierRule: (rule: ShopOverrideRuleDto) => void;
  onDeleteCarrierRule: (rule: ShopOverrideRuleDto) => void;
  onOpenTarifRule: (pricingId: string) => void;
  onEditTarifRule: (rule: ShopOverrideRuleDto) => void;
  deleteRulePending: boolean;
  editCarrierPricingsTransport: (params: {
    pricings: unknown[];
    carrierId: string;
  }) => void;
  deleteCarrierPricing: (params: {
    carrierId: string;
    carrierPricingId: string;
  }) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  shop: any;
}

function CarrierReturnFeeSection({
  carrier,
  carrierRules,
  getTarifRules,
  formatFeeValue,
  isAdmin,
  pricingState,
  onOpenPricing,
  onClosePricing,
  onOpenCarrierRule,
  onEditCarrierRule,
  onDeleteCarrierRule,
  onOpenTarifRule,
  onEditTarifRule,
  deleteRulePending,
  editCarrierPricingsTransport,
  deleteCarrierPricing,
  shop,
}: CarrierReturnFeeSectionProps) {
  const t = useTranslation();
  const formatPrice = useFormatPrice();
  const isCreatingPricing =
    pricingState.carrierId === carrier.id && pricingState.pricingIndex === -1;
  const [pendingDeletePricingId, setPendingDeletePricingId] = useState<
    string | null
  >(null);

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b">
        <CarrierLabel carrier={carrier} />

        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1 border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
              onClick={onOpenCarrierRule}
            >
              <Plus className="h-3 w-3" />
              {carrierRules.length > 0
                ? t("settings.carriers.returnFeeOverride.rulesCount_other", {
                    count: carrierRules.length,
                  })
                : t("settings.carriers.returnFeeOverride.addCarrierRule")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => onOpenPricing(-1)}
            >
              {t("settings.carriers.returnFeeOverride.addTarif")}
            </Button>
          </div>
        )}
      </div>

      {/* Carrier-level rules */}
      {carrierRules.length > 0 && (
        <div className="bg-violet-50/40 border-b px-3 py-2 flex flex-wrap items-center gap-1.5">
          {carrierRules.map((rule) => (
            <Badge
              key={rule.id}
              variant="outline"
              title={conditionsSummary(
                rule.conditions,
                t as (k: string) => string,
              )}
              className="cursor-pointer gap-1 text-xs font-normal py-0.5 bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100"
              onClick={() => onEditCarrierRule(rule)}
            >
              {rule.name}
              {formatFeeValue(rule) && (
                <span className="font-medium"> → {formatFeeValue(rule)}</span>
              )}
              <PencilIcon className="h-2.5 w-2.5 opacity-50" />
            </Badge>
          ))}
        </div>
      )}

      {/* New pricing form */}
      {isCreatingPricing && (
        <div className="p-3 border-b bg-gray-50">
          <PricingEdition
            carrier={carrier}
            shop={shop}
            setEditingIsOpen={(open) => !open && onClosePricing()}
            onSubmit={editCarrierPricingsTransport}
          />
        </div>
      )}

      {/* Pricing rows */}
      <div className="divide-y">
        {carrier.pricings.map((pricing, index) => {
          const pRules = getTarifRules(pricing.id);
          const isEditingThis =
            pricingState.carrierId === carrier.id &&
            pricingState.pricingIndex === index;

          if (isEditingThis) {
            return (
              <div key={pricing.id} className="p-3 bg-gray-50">
                <PricingEdition
                  carrier={carrier}
                  pricing={pricing}
                  shop={shop}
                  setEditingIsOpen={(open) => !open && onClosePricing()}
                  setEditingPricingIndex={() => onClosePricing()}
                  onSubmit={editCarrierPricingsTransport}
                />
              </div>
            );
          }

          return (
            <div
              key={pricing.id}
              className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/30 group"
            >
              {/* Origin */}
              <span className="text-xs w-24 shrink-0 text-muted-foreground">
                {countryFromISO(pricing.origin)}
              </span>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
              {/* Destination */}
              <span className="text-xs w-24 shrink-0 text-muted-foreground">
                {countryFromISO(pricing.destination)}
              </span>
              {/* Base price */}
              <span className="text-xs font-medium flex-1">
                {formatPrice({
                  amount: pricing.price,
                  currency: pricing.currency,
                })}
              </span>

              {/* Tarif-level rule badges */}
              {pRules.map((rule) => (
                <Badge
                  key={rule.id}
                  variant="outline"
                  title={conditionsSummary(
                    rule.conditions,
                    t as (k: string) => string,
                  )}
                  className="cursor-pointer text-xs gap-1 font-normal shrink-0 py-0.5 bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100"
                  onClick={() => onEditTarifRule(rule)}
                >
                  {rule.name}
                  {formatFeeValue(rule) && (
                    <span className="font-medium">
                      {" "}
                      → {formatFeeValue(rule)}
                    </span>
                  )}
                  <PencilIcon className="h-2.5 w-2.5 opacity-50" />
                </Badge>
              ))}

              {/* Actions */}
              {isAdmin && (
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs px-1.5 gap-1 text-violet-600 hover:text-violet-800 hover:bg-violet-50"
                    onClick={() => onOpenTarifRule(pricing.id)}
                  >
                    <Plus className="h-3 w-3" />
                    {t("settings.carriers.returnFeeOverride.addTarifRule")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => onOpenPricing(index)}
                  >
                    <PencilIcon className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-red-500"
                    onClick={() => setPendingDeletePricingId(pricing.id)}
                  >
                    <TrashIcon className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          );
        })}

        {carrier.pricings.length === 0 && !isCreatingPricing && (
          <p className="text-xs text-muted-foreground px-3 py-2 italic">—</p>
        )}
      </div>

      <AlertDialog
        open={pendingDeletePricingId !== null}
        onOpenChange={(open) => !open && setPendingDeletePricingId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("settings.carriers.returnFeeOverride.deleteTarifTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings.carriers.returnFeeOverride.deleteTarifDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("misc.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (pendingDeletePricingId) {
                  deleteCarrierPricing({
                    carrierId: carrier.id,
                    carrierPricingId: pendingDeletePricingId,
                  });
                }
                setPendingDeletePricingId(null);
              }}
            >
              {t("misc.remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Public export ───────────────────────────────────────────────────────────

export function ReturnFeeOverrideRules() {
  return (
    <OverrideRulesProvider>
      <ReturnFeePricingsCardContent />
    </OverrideRulesProvider>
  );
}
